import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import cx from 'classnames';
import TourStopMediaGallery from '../TourStopMediaGallery/TourStopMediaGallery';
import BoxHeader from './BoxHeader/BoxHeader';
import MediaButtons from '../TourStopMediaBox/MediaButtons/MediaButtons';
import AudioPlayer from '../../../Media/AudioPlayer/AudioPlayer';
import ButtonHoverRender from '../../../Buttons/ButtonHover/ButtonHoverRender';
import ButtonHoverPrimary from '../../../Buttons/ButtonHover/ButtonHoverPrimary';
import Footer from '../Footer/Footer';
import Icon from '../../../Icon/Icons';
import { customGaEvent, GA_EVENTS } from '../../../../utils/ga';
import { scrollTop } from 'utils/scrollUtils';

import 'react-quill/dist/quill.snow.css';
import './TourStopMediaBox.scss';

class TourStopMediaBox extends Component {
  constructor(props) {
    super(props);

    const isMobile = window.innerWidth < 1025;

    this.state = {
      isMobile,
      minimized: isMobile,
      showGallery: false,
      showTranscript: false,
      activeTab: '',
      activeMediaIndex: 0,
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps, prevState) {
    const { showGallery, activeMediaIndex, activeTab } = this.state;
    const { activeStop, stopImages, stopVideos, stopDescriptionMinimized } = this.props;

    if (prevProps.activeStop !== activeStop) {
      this.setState({ showTranscript: false });
      scrollTop(this.descRef);
    }

    if (prevProps.stopDescriptionMinimized !== stopDescriptionMinimized && window.innerWidth >= 1025) {
      this.setState({ minimized: stopDescriptionMinimized });
    }

    if (!prevState.showGallery && showGallery) {
      // focus on gallery stop name on opening of media gallery
      setTimeout(() => this.galleryHeader && this.galleryHeader.focus(), 250); // allow for modal transition in before setting focus
    }

    if (
      prevState.activeMediaIndex !== activeMediaIndex ||
      prevState.activeTab !== activeTab
    ) {
      const currentMediaList = activeTab === 'Images' ? stopImages : stopVideos;
      const { name } = currentMediaList[activeMediaIndex] || {};
      name &&
        activeTab &&
        customGaEvent({
          action: `View Additional Media (${activeTab})`,
          label: name,
        });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { activeStop, isMainMediaPlaying, stopDescription, t } = this.props;
    if (
      activeStop !== nextProps.activeStop ||
      this.handleComponentUpdates(nextState) ||
      stopDescription !== nextProps.stopDescription ||
      t !== nextProps.t
    ) {
      return true;
    }
    if (isMainMediaPlaying !== nextProps.isMainMediaPlaying) return true;

    return false;
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleComponentUpdates = nextState => {
    for (const prop in this.state) {
      if (this.state[prop] !== nextState[prop]) return Boolean(prop);
    }
  };

  toggleMediaBox = e => {
    const {
      displayMiniMapButton,
      hideStopDescription,
      showStopDescription,
    } = this.props;
    const { minimized } = this.state;
    const { name } = e.target;
    this.setState(
      ({ minimized }) => ({ minimized: !minimized }),
      () => {
        // KEEP this setState callback, manual focus() methods depend on it
        let boxState;
        if (minimized) {
          boxState = 'Close';
          hideStopDescription();
        } else {
          boxState = 'Open';
          showStopDescription();
        }
        customGaEvent({
          action: GA_EVENTS.MEDIA_MINIMIZE,
          label: boxState,
        });
      }
    );
    // handle minimize & maximize button click focus on mediabox
    this.handleBoxToggleFocus(name);
    typeof displayMiniMapButton === 'function' &&
      displayMiniMapButton(!minimized);
  };

  handleBoxToggleFocus = btnName => {
    const { togglePanoKeyboardControl } = this.props;

    setTimeout(() => {
      const focusElName = btnName === 'minimize' ? 'maximize' : 'minimize';
      this[focusElName] && this[focusElName].focus(); // refs: this.minimize || this.maximize
    }, 500); // timeout allows DOMnode to render otherwise returns undefined/null
    togglePanoKeyboardControl();
  };

  toggleGallery = e => {
    const { name } = e.target;
    this.setState(({ showGallery }) => ({
      showGallery: !showGallery,
      activeTab: name,
    }));
  };

  toggleTranscript = () => {
    this.setState(
      ({ showTranscript }) => ({ showTranscript: !showTranscript }),
      () => {
        const transcriptState = this.state.showTranscript ? 'Open' : 'Close';
        customGaEvent({
          action: GA_EVENTS.AUDIO_TRANSCRIPT,
          label: transcriptState,
        });
        this.focusMinimizeBtn();
      }
    );
  };

  focusMinimizeBtn = () => {
    const { showTranscript } = this.state;
    if (showTranscript) {
      setTimeout(() => {
        this.minimize.focus();
      }, 0);
    }
  };

  toggleActiveTab = activeTab => {
    this.setState({ activeTab });
  };

  getMediaIndex = activeMediaIndex => {
    this.setState({ activeMediaIndex });
  };

  handleResize = () => {
    const isMobile = window.innerWidth < 1025;
    const newMinimized = isMobile ? true : this.props.stopDescriptionMinimized;
    this.setState({
      isMobile,
      minimized: newMinimized
    });
  }

  render() {
    const { activeTab, minimized, showGallery, showTranscript, isMobile } = this.state;
    const {
      autoPlayAudio,
      isMainMediaPlaying,
      panoId,
      primaryColor,
      stopAudio = [],
      stopDescription,
      stopImages = [],
      stopName,
      stopVideos,
      toggleMainMediaStatus,
      tourId,
      tourName,
      aid,
      previewTour,
      hasLanguages,
      t,
    } = this.props;
    const hasText =
      stopDescription || (stopAudio[0]?.transcript && showTranscript);

    const descClasses = cx({
      'stop-desc ql-editor': hasText,
    });

    const stopImagesFiltered = stopImages.filter(image => image?.id !== panoId);
    const noMedia =
      !stopImagesFiltered?.length && !stopVideos?.length && !stopAudio?.length;

    const noContent = !Boolean(stopDescription) && noMedia;

    const stopMediaClasses = cx({
      'stop-media-hidden': minimized,
      'stop-media-show': !minimized,
      'title-with-desc': stopDescription && noMedia,
      'title-with-media': !Boolean(stopDescription) && !noMedia,
      'title-with-desc-media': Boolean(stopDescription) && !noMedia,
      'title-with-all-media':
        !Boolean(stopDescription) &&
        stopAudio.length &&
        (stopImages.length || stopVideos.length),
      'audio-transcription-shown': Boolean(stopAudio[0]) && showTranscript,
      'no-content': noContent && !hasLanguages,
    });

    return (
      <React.Fragment>
        {(minimized || (isMobile && !minimized && noContent)) && (
          <ButtonHoverRender
            render={({ onEnter, onLeave, hovering }) => (
              <ButtonHoverPrimary
                aria-label={t('publicTour.ada.tourContent.maximize')}
                backgroundColor="#fff"
                className="stop-media-hide tour-content"
                color={primaryColor}
                data-cy="tour-stop-media-box__maximize-btn"
                handleClick={this.toggleMediaBox}
                hovering={hovering}
                myRef={btn => (this.maximize = btn)}
                name="maximize"
                onEnter={onEnter}
                onLeave={onLeave}
                active={!minimized}
              >
                <Icon>info_outline</Icon>
              </ButtonHoverPrimary>
            )}
          />
        )}
        {(!minimized && (!noContent || hasLanguages || isMobile)) && (
          <main
            className={stopMediaClasses}
            aria-label={t('publicTour.ada.tourContent.additionalContent')}
          >
            {(!noContent || hasLanguages || (!isMobile && !minimized)) && (
              <BoxHeader
                showTranscript={showTranscript}
                toggleMediaBox={this.toggleMediaBox}
                primaryColor={primaryColor}
                ariaExpanded={!showTranscript && Boolean(stopDescription)}
                minimizeBtnRef={btn => (this.minimize = btn)}
                hasDescription={Boolean(stopDescription)}
                noContent={noContent}
              />
            )}
            <React.Fragment>
              <div
                className={descClasses}
                ref={node => (this.descRef = node)}
                data-testid="media-box-text-content"
              >
                {!showTranscript && Boolean(stopDescription) && (
                  <article
                    id="tour-stop-description"
                    aria-label={t('publicTour.ada.tourContent.stopDescription')}
                    data-cy="tour-stop-description"
                    dangerouslySetInnerHTML={{
                      __html: stopDescription,
                    }}
                  />
                )}
                {showTranscript && stopAudio[0] && stopAudio[0].transcript && (
                  <article
                    id="focus-audio-transcript"
                    className="audio-transcript transcript-shown"
                  >
                    <h4>{t('publicTour.media.audioTranscript')}</h4>
                    <p>{stopAudio[0].transcript}</p>
                  </article>
                )}
              </div>
            </React.Fragment>
            {Boolean(stopAudio && stopAudio[0]) && (
              <AudioPlayer
                autoPlay={autoPlayAudio}
                tourId={tourId}
                aid={aid}
                tourName={tourName}
                width={150}
                tracks={stopAudio}
                primaryColor={primaryColor}
                toggleTranscript={this.toggleTranscript}
                showTranscript={showTranscript}
                displayIcon
                toggleMainMediaStatus={toggleMainMediaStatus}
                isMainMediaPlaying={isMainMediaPlaying}
                previewTour={previewTour}
              />
            )}
            <MediaButtons
              stopImages={stopImagesFiltered}
              stopVideos={stopVideos}
              primaryColor={primaryColor}
              toggleGallery={this.toggleGallery}
              tourName={tourName}
              tourId={tourId}
              aid={aid}
            />
            <TourStopMediaGallery
              activeTab={activeTab === 'Images' ? 0 : 1}
              closeModal={this.toggleGallery}
              stopImages={stopImagesFiltered}
              stopVideos={stopVideos}
              primaryColor={primaryColor}
              stopName={stopName}
              isOpen={showGallery}
              myRef={el => (this.galleryHeader = el)}
              getMediaIndex={this.getMediaIndex}
              toggleActiveTab={this.toggleActiveTab}
            />
            {isMobile && !minimized && (
              <div className={`footer-wrapper ${!noContent ? 'with-content' : ''}`}>
                <Footer variant="mobile" />
              </div>
            )}
          </main>
        )}
      </React.Fragment>
    );
  }
}

export default withTranslation()(TourStopMediaBox);
